import apiClient from './apiClient';
import { structuredDetail, validationMessage } from './structuredDetail';
import { normalizeGender } from '../../utils/gender';
import { ADMISSION_ACCESS_ERROR_CODE, PROVIDER_OUT_OF_SCOPE_APPOINTMENT_COPY } from '../../constants/admission';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * Appointment API — REST endpoints for appointment management
 *
 * Base paths:
 *   POST   /api/organisation/appointments              — Book appointment
 *   GET    /api/organisation/appointments               — List (date range)
 *   GET    /api/organisation/appointments/check          — Auto-detect by mobile
 *   GET    /api/organisation/appointments/slots           — Suggested slots
 *   PUT    /api/organisation/appointments/:id             — Update (reschedule)
 *   PUT    /api/organisation/appointments/:id/cancel      — Cancel
 *   POST   /api/organisation/appointments/:id/checkin     — Check in
 *   POST   /api/organisation/appointments/:id/rebook      — Rebook cancelled
 * ═══════════════════════════════════════════════════════════════════════
 */

// ── Error helper ─────────────────────────────────────────────────────
// `detail` is a string (plain HTTPException), a `{code, message, ...extras}`
// object (admission-access C-1 gates) or a validation array. The message is
// ALWAYS a string — never "[object Object]" (bk-1665) — and a structured
// `detail.code` is kept on `err.code`. PROVIDER_OUT_OF_SCOPE on an existing
// appointment (cancel / rebook / time edit / check-in on a stale screen) shows
// the ratified sentence, never the wire message.
function buildApiError(error) {
  const data = error?.response?.data;
  const detail = data?.detail;
  const structured = structuredDetail(error);
  const code = structured?.code;
  const msg =
    (code === ADMISSION_ACCESS_ERROR_CODE.PROVIDER_OUT_OF_SCOPE ? PROVIDER_OUT_OF_SCOPE_APPOINTMENT_COPY : null) ||
    (typeof detail === 'string' ? detail : null) ||
    structured?.message ||
    validationMessage(detail) ||
    data?.message ||
    error?.message ||
    'Unknown error';
  const err = new Error(msg);
  err.status = error?.response?.status;
  err.data = data;
  if (code) err.code = code;
  if (structured?.patient_id) err.patient_id = structured.patient_id;
  return err;
}

// ── Book Appointment ─────────────────────────────────────────────────

/**
 * POST /api/organisation/appointments
 *
 * @param {Object} payload
 *   - patient_name (string, required)
 *   - patient_mobile (string, required)
 *   - doctor_member_id (string, required)
 *   - appointment_time (string, required — "HH:MM" or "H:MM AM/PM")
 *   - appointment_date (string, optional — defaults to today)
 *   - age (number, optional)
 *   - dob (string, optional)
 *   - gender (string, optional)
 *   - notes (string, optional)
 *   - appointment_type (string: CONSULTATION | PROCEDURE)
 *   - duration_minutes (number: total block length)
 *   - procedure_id / procedure_name (back-compat single fields)
 *   - procedures (array of { procedure_id, name, duration_minutes }, optional)
 * @returns {{ appointment, patient }}
 */
export const bookAppointment = async (payload) => {
  try {
    const body = payload.gender === undefined ? payload : { ...payload, gender: normalizeGender(payload.gender) || null };
    const response = await apiClient.post('/api/organisation/appointments', body);
    return response.data;
  } catch (error) {
    console.error('[AppointmentAPI] Error booking appointment:', error);
    throw buildApiError(error);
  }
};

// ── List Appointments ────────────────────────────────────────────────

/**
 * GET /api/organisation/appointments?start_date=&end_date=&doctor_member_id=&status=
 *
 * @param {Object} filters
 *   - start_date (string, optional)
 *   - end_date (string, optional)
 *   - doctor_member_id (string, optional)
 *   - status (string, optional — BOOKED|CHECKED_IN|CANCELLED|NO_SHOW|EXPIRED)
 * @returns {{ appointments: [...], count, start_date, end_date }}
 */
export const fetchAppointments = async (filters = {}) => {
  try {
    const params = {};
    if (filters.start_date) params.start_date = filters.start_date;
    if (filters.end_date) params.end_date = filters.end_date;
    if (filters.doctor_member_id) params.doctor_member_id = filters.doctor_member_id;
    if (filters.status) params.status = filters.status;

    const response = await apiClient.get('/api/organisation/appointments', { params });
    return response.data;
  } catch (error) {
    console.error('[AppointmentAPI] Error fetching appointments:', error);
    throw buildApiError(error);
  }
};

// ── Auto-Detect Appointment by Mobile ────────────────────────────────

/**
 * GET /api/organisation/appointments/check?mobile=
 *
 * Returns the matching active appointment (if any) within ±60 min window.
 * @param {string} mobile — 10-digit mobile number
 * @returns {{ found: boolean, appointment: {...} | null }}
 */
export const checkAppointmentByMobile = async (mobile) => {
  try {
    const response = await apiClient.get('/api/organisation/appointments/check', {
      params: { mobile },
    });
    return response.data;
  } catch (error) {
    console.error('[AppointmentAPI] Error checking appointment:', error);
    throw buildApiError(error);
  }
};

// ── Suggested Slots ──────────────────────────────────────────────────

/**
 * GET /api/organisation/appointments/slots?doctor_member_id=&date=
 *
 * @returns {{ slots: [{ hour, label, appointment_count }], date, doctor_member_id }}
 */
export const fetchSuggestedSlots = async (doctorId, date) => {
  try {
    const params = { doctor_id: doctorId };
    if (date) params.date = date;
    const response = await apiClient.get('/api/organisation/appointments/slots', { params });
    return response.data;
  } catch (error) {
    console.error('[AppointmentAPI] Error fetching slots:', error);
    throw buildApiError(error);
  }
};

// ── Update Appointment (Reschedule) ──────────────────────────────────

/**
 * PUT /api/organisation/appointments/:id
 *
 * @param {string} appointmentId
 * @param {Object} updates — { appointment_time, appointment_date, notes }
 * @returns {{ appointment }}
 */
export const updateAppointment = async (appointmentId, updates) => {
  try {
    const response = await apiClient.put(
      `/api/organisation/appointments/${appointmentId}`,
      updates,
    );
    return response.data;
  } catch (error) {
    console.error('[AppointmentAPI] Error updating appointment:', error);
    throw buildApiError(error);
  }
};

// ── Cancel Appointment ───────────────────────────────────────────────

/**
 * PUT /api/organisation/appointments/:id/cancel
 *
 * @param {string} appointmentId
 * @returns {{ appointment }}
 */
export const cancelAppointment = async (appointmentId) => {
  try {
    const response = await apiClient.put(
      `/api/organisation/appointments/${appointmentId}/cancel`,
    );
    return response.data;
  } catch (error) {
    console.error('[AppointmentAPI] Error cancelling appointment:', error);
    throw buildApiError(error);
  }
};

// ── Check In Appointment ─────────────────────────────────────────────

/**
 * POST /api/organisation/appointments/:id/checkin
 *
 * Creates a visit from the appointment + marks CHECKED_IN.
 *
 * D14 (CONSENT-CAPTURE) — check-in builds the registration request
 * server-side, so it is a 6th patient-creating/registering path; the
 * backend adds the same required-and-true `consents` validation here as
 * the other three write paths. ONLY the consent value ever reaches the
 * server (the staff read-aloud instruction is frontend-only).
 *
 * @param {string} appointmentId
 * @param {boolean} consentGranted
 * @returns {{ appointment, visit }}
 */
export const checkInAppointment = async (appointmentId, consentGranted) => {
  try {
    const response = await apiClient.post(
      `/api/organisation/appointments/${appointmentId}/checkin`,
      { consents: { digital_records: !!consentGranted } },
    );
    return response.data;
  } catch (error) {
    console.error('[AppointmentAPI] Error checking in appointment:', error);
    throw buildApiError(error);
  }
};

// ── Rebook Cancelled Appointment ─────────────────────────────────────

/**
 * POST /api/organisation/appointments/:id/rebook
 *
 * Clones a cancelled appointment into a new BOOKED appointment.
 * Only works within 60-min window of cancellation.
 * @param {string} appointmentId
 * @returns {{ appointment }}
 */
export const rebookAppointment = async (appointmentId) => {
  try {
    const response = await apiClient.post(
      `/api/organisation/appointments/${appointmentId}/rebook`,
    );
    return response.data;
  } catch (error) {
    console.error('[AppointmentAPI] Error rebooking appointment:', error);
    throw buildApiError(error);
  }
};

// ── Impacted Appointments (Confirm Appointments widget) ──────────────

/**
 * GET /api/organisation/appointments/impacted?doctor_id=
 *
 * BOOKED appointments overlapping a doctor's blocking calendar blocks.
 * @param {Object} filters — { doctor_id }
 * @returns {{ impacted: Array, count: number }}
 */
export const fetchImpactedAppointments = async (filters = {}) => {
  try {
    const params = {};
    if (filters.doctor_id) params.doctor_id = filters.doctor_id;
    const response = await apiClient.get('/api/organisation/appointments/impacted', { params });
    return response.data;
  } catch (error) {
    console.error('[AppointmentAPI] Error fetching impacted appointments:', error);
    throw buildApiError(error);
  }
};

/**
 * POST /api/organisation/appointments/impacted/:id/dismiss?block_id=
 *
 * Dismiss one impact notification (does NOT touch the appointment).
 * @param {string} appointmentId
 * @param {string} blockId
 */
export const dismissImpact = async (appointmentId, blockId) => {
  try {
    const response = await apiClient.post(
      `/api/organisation/appointments/impacted/${appointmentId}/dismiss`,
      null,
      { params: { block_id: blockId } },
    );
    return response.data;
  } catch (error) {
    console.error('[AppointmentAPI] Error dismissing impact:', error);
    throw buildApiError(error);
  }
};
